import { clampUnitInterval } from './EngagementScore';

export interface BrowserVisibilitySnapshot {
  readonly supported: boolean;
  readonly hasValidSample: boolean;
  readonly documentVisible: boolean;
  readonly elementVisibilityRatio?: number;
  readonly detached: boolean;
  readonly invalid: boolean;
}

export interface BrowserVisibilityTracker {
  snapshot(): BrowserVisibilitySnapshot;
  destroy(): void;
}

const UNSUPPORTED_SNAPSHOT: BrowserVisibilitySnapshot = Object.freeze({
  supported: false,
  hasValidSample: false,
  documentVisible: true,
  detached: false,
  invalid: false,
});

export function createBrowserVisibilityTracker(videoElement?: HTMLVideoElement): BrowserVisibilityTracker {
  const canObserveVisibility =
    !!videoElement &&
    typeof document !== 'undefined' &&
    typeof document.addEventListener === 'function' &&
    typeof IntersectionObserver !== 'undefined';

  if (!canObserveVisibility) {
    return {
      snapshot: () => UNSUPPORTED_SNAPSHOT,
      destroy: () => undefined,
    };
  }

  let documentVisible = document.visibilityState !== 'hidden';
  let hasValidSample = false;
  let elementVisibilityRatio: number | undefined;
  let detached = false;
  let invalid = false;

  const onDocumentVisibilityChange = () => {
    documentVisible = document.visibilityState !== 'hidden';
  };

  const observer = new IntersectionObserver(entries => {
    const matchedEntry = entries.find(entry => entry.target === videoElement);
    if (!matchedEntry) {
      return;
    }

    const ratio = clampUnitInterval(matchedEntry.intersectionRatio);
    if (ratio === undefined) {
      invalid = true;
      return;
    }

    hasValidSample = true;
    elementVisibilityRatio = ratio;
    detached = !videoElement.isConnected;
  });

  observer.observe(videoElement);
  document.addEventListener('visibilitychange', onDocumentVisibilityChange);

  return {
    snapshot: (): BrowserVisibilitySnapshot => {
      detached = detached || (typeof videoElement.isConnected === 'boolean' && !videoElement.isConnected);
      return {
        supported: true,
        hasValidSample,
        documentVisible,
        elementVisibilityRatio,
        detached,
        invalid,
      };
    },

    destroy(): void {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
    },
  };
}
